import s from './InviteUsersModal.module.scss';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod/dist/zod';
import { GeneralModalProps } from '@/types/modals';
import { ModalWrapper } from '../ModalWrapper/ModalWrapper';
import { useFetcherWithUtils } from '@/hooks/useFetcherWithUtils';
import { InviteUsersWidget } from '@/components/InviteUsersWidget/InviteUsersWidget';
import { ModalBottomActions } from '../ModalBottomActions/ModalBottomActions';
import { UserRole, userRoles } from '@/types/types';

type Props = GeneralModalProps &
  (
    | { usersNum: number; invitationsNum: number }
    | { usersNum?: never; invitationsNum?: never }
  );

const invitationsSchema = z.object({
  emails: z.object({ email: z.string().email().or(z.string().length(0)) }).array(),
  role: z.enum(userRoles),
});

export type InvitationsSchema = z.infer<typeof invitationsSchema>;

export function InviteUsersModal({ setClose, isOpen, ...rest }: Props) {
  return (
    <ModalWrapper className={s.modal} isOpen={isOpen} onRequestClose={setClose}>
      <InviteUsersModalContent setClose={setClose} {...rest} />
    </ModalWrapper>
  );
}

type ContentProps = Pick<Props, 'setClose' | 'invitationsNum' | 'usersNum'>;

function InviteUsersModalContent({ setClose, invitationsNum, usersNum }: ContentProps) {
  const [fetcher, isLoading] = useFetcherWithUtils(setClose);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
    getValues,
  } = useForm<InvitationsSchema>({
    resolver: zodResolver(invitationsSchema),
    defaultValues: { emails: [{ email: '' }], role: 'viewer' },
  });

  const onSubmit = (data: InvitationsSchema) => {
    const formData = new FormData();
    const emails = data.emails.map((email) => email.email);
    const role = data.role;
    formData.append('emails', JSON.stringify(emails));
    formData.append('role', role);

    if (usersNum !== undefined && invitationsNum !== undefined) {
      formData.append('usersNum', usersNum.toString());
      formData.append('invitationsNum', invitationsNum.toString());
    }
    fetcher.submit(formData, { method: 'post' });
  };

  const onRoleValChange = async (e: string) => {
    setValue('role', e as UserRole);
    await trigger('role');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InviteUsersWidget
        control={control}
        register={register}
        errors={errors}
        onRoleValChange={onRoleValChange}
        roleValue={getValues('role')}
      />
      <ModalBottomActions isLoading={isLoading} setClose={setClose} />
    </form>
  );
}
